import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/autoplay';
import { Pagination, Autoplay } from 'swiper/modules';
import axios from "axios";
import Navbar from '../Components/Navbar';
import AddAddress from '../Components/AddAddress';
import Footer from '../Components/Footer';
import options from '../Images/options.png';
import UploadArtwork from '../Images/UploadArtwork.png';
import edit from '../Images/edit-text.png';
import Select from 'react-select';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json'; // or ar.json for Arabic
import { pushDataLayerEvent } from '../utils/tracking';
countries.registerLocale(enLocale); 

const CartPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = location.state?.token || localStorage.getItem('token'); // Get token from state or localStorage
  const [openDropdown, setOpenDropdown] = useState(null);
  const [products, setProducts] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [delivery, setDelivery] = useState(0);
  const [VAT, setVAT] = useState(0);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [productToDuplicate, setProductToDuplicate] = useState(null);
  const [showSaveForLaterConfirm, setShowSaveForLaterConfirm] = useState(false);
  const [productToSaveForLater, setProductToSaveForLater] = useState(null);
  const [cartIsEmpty, setCartIsEmpty] = useState(false); // New state for empty cart
  const [uploadError, setUploadError] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filesToUpload, setFilesToUpload] = useState({ file1: null, file2: null });
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState(null);
  const [selectedBillingAddress, setSelectedBillingAddress] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false); // Modal visibility
  const [addressType, setAddressType] = useState(''); // To differentiate delivery or billing
  const [allAddresses, setAllAddresses] = useState([]);
  const [saveForLaterMessage, setSaveForLaterMessage] = useState('');
  const [duplicateMessage, setDuplicateMessage] = useState('');
  const [deleteMessage, setDeleteMessage] = useState('');
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false); // To manage loading state
  const [checkoutError, setCheckoutError] = useState(''); // To manage API errors
  const [popupVisible, setPopupVisible] = useState(false);
  const [couponCode, setCouponCode] = useState(''); // Store the coupon code
  const [couponMessage, setCouponMessage] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe'); 
  const [popupMessage, setPopupMessage] = useState('');
  const [unavailableMessage, setUnavailableMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [hasInactiveProduct, setHasInactiveProduct] = useState(false);
  const hasTrackedBeginCheckoutRef = useRef(false);
  const countryOptions = Object.entries(countries.getNames('en')).map(([code, name]) => ({
    value: name,
    label: name,
  }));

  const [formData, setFormData] = useState({
    name: "",
    city: "",
    county: "",
    postcode: "",
    street: "",
    building: "",
    floor: "",
    apartment: "",
    phone_number: "",
    note: "",
    isDefault: true,
    with_logo: true,
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [postcodeError, setPostcodeError] = useState("");
  const [geolocation, setGeolocation] = useState({ latitude: "", longitude: "" });

  const buildCartTrackingItem = (product) => ({
    item_id: String(product.product_id || product.product?.id || product.id || ''),
    item_name: product.product?.name || product.name || '',
    item_variant: [product.paper_weight, product.paper_type].filter(Boolean).join(' ').trim() || product.size || '',
    price: Number(parseFloat(product.product_price || 0).toFixed(2)),
    quantity: Number(product.quantity || 1),
    item_category: product.product?.category?.name || product.category_name || '',
  });
  
  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };
  
  // Validate postcode and fetch details
  const handlePostcodeLookup = async () => {
    const postcode = formData.postcode.trim();
    if (!postcode) {
      if (formData.county === "United Kingdom") {
        setPostcodeError("Postcode is required for United Kingdom.");
      } else {
        setPostcodeError(""); // Clear for non-UK
      }
      return;
    }
  
    try {
      const response = await axios.get(`https://api.postcodes.io/postcodes/${postcode}`);
      if (response.data.status === 200) {
        const { latitude, longitude, admin_district, country } = response.data.result;
        setGeolocation({ latitude, longitude });
        setFormData((prevData) => ({
          ...prevData,
          city: admin_district,
          county: country,
        }));
        setPostcodeError("");
      } else {
        if (formData.county === "United Kingdom") {
          setPostcodeError("Invalid postcode. Please check and try again.");
        }
      }
    } catch (err) {
      if (formData.county === "United Kingdom") {
        setPostcodeError("Please provide a valid zipcode in UK.");
      }
    }
  };
  useEffect(() => {
    if (!formData.postcode.trim()) return;
  
    const delay = setTimeout(() => {
      handlePostcodeLookup();
    }, 800); 
  
    return () => clearTimeout(delay); 
  }, [formData.postcode]);

  useEffect(() => {
    if (formData.county === "United Kingdom" && formData.postcode.trim()) {
      const delay = setTimeout(() => {
        handlePostcodeLookup();
      }, 800);
  
      return () => clearTimeout(delay);
    }
  }, [formData.postcode, formData.county]);
  
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
  
    // Ensure autofilled values are captured
    const form = document.querySelector('form');
    const formElements = Array.from(form.elements);
  
    const syncedData = { ...formData };
    formElements.forEach((el) => {
      if (el.name && el.value !== undefined) {
        syncedData[el.name] = el.type === 'checkbox' ? el.checked : el.value;
      }
    });
  
    const requestBody = {
      name: syncedData.name,
      country: syncedData.county,
      city: syncedData.city,
      postcode: syncedData.postcode,
      street: " ",
      building: syncedData.building,
      floor: " ",
      apartment: syncedData.apartment,
      phone_number: syncedData.phone_number,
      note: syncedData.note,
      is_default: syncedData.isDefault,
      with_logo: syncedData.with_logo,
    };
  
    try {
      const response = await axios.post(
        "https://backend.printtrio.co.uk/api/profile/add-address",
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
  
      if (response.data.success) {
        await fetchCartData();
        setShowAddressModal(false); // Close modal after success
  
        setFormData({
          name: "",
          city: "",
          county: "",
          postcode: "",
          street: "",
          building: "",
          floor: "",
          apartment: "",
          phone_number: "",
          note: "",
          isDefault: false,
          with_logo: true,
        });
      } else {
        console.error("Failed to add address");
      }
    } catch (err) {
      console.error("Error adding address:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  
  useEffect(() => {
    // Check query parameters for payment status
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');

    if (status === 'failure') {
      setPopupMessage('Payment Failed. Please try again.');
    }
  }, []); 


  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


// Fetch cart data from API
const fetchCartData = async () => {
  if (!token) {
    setError('No token found. Please log in first.');
    navigate('/'); // Redirect to login if no token
    return;
  }
  setIsLoading(true);
  try {
    const response = await fetch('https://backend.printtrio.co.uk/api/cart/user-cart', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (response.ok && data.success) {

      const addresses = data.data.all_address || [];

      // Find default delivery and billing addresses
      const defaultAddress = addresses.find((address) => address.is_default === 1);

      // Set states for default delivery and billing addresses
      if (defaultAddress) {
        setSelectedDeliveryAddress(defaultAddress); // Use the default address for delivery
        setSelectedBillingAddress(defaultAddress);  // Use the same default address for billing
      }

      
      setProducts(data.data.cartItems);
      setSubtotal(data.data.sub_total);
      setDelivery(data.data.delivery);
      setVAT(data.data.VAT);
      setTotal(data.data.total_order);
      setAllAddresses(addresses);
      setCartIsEmpty(data.data.cartItems.length === 0);
      
      const inactiveProductExists = data.data.cartItems.some(item => item.product?.is_active === 0);
      setHasInactiveProduct(inactiveProductExists);
      // Check for products with service_id === 1 or 2 and handle file errors 173.203.187.73
      for (const product of data.data.cartItems) {
        if (product.service_id === 1 || product.service_id === 2) {
          // Check if file1 or file2 are empty objects (e.g., {})
          if (Object.keys(product.file1 || {}).length === 0 && Object.keys(product.file2 || {}).length === 0) {
            // Set error when no files are uploaded
            setUploadError((prev) => ({
              ...prev,
              [product.id]: 'No file was uploaded. Please upload the required files. You can upload files from here.',
            }));
          } else {
            // Clear the error if files are present
            setUploadError((prev) => {
              const { [product.id]: removedError, ...rest } = prev;
              return rest; // Remove error for this product
            });
          }
        }
      }
    } else {
      setCartIsEmpty(true); // Mark cart as empty if no items
    }
  } catch (error) {
    // setError('An error occurred while fetching the cart data.');
  } finally {
    setIsLoading(false);   // ← turn off spinner no matter what
  }
};



useEffect(() => {
  fetchCartData(); // Initial fetch when component mounts
}, [location.state, navigate, token]);

useEffect(() => {
  if (isLoading) {
    return;
  }

  if (!products.length) {
    hasTrackedBeginCheckoutRef.current = false;
    return;
  }

  if (hasTrackedBeginCheckoutRef.current) {
    return;
  }

  const checkoutItems = products.map(buildCartTrackingItem);
  const checkoutValue = Number(parseFloat(total || 0).toFixed(2));
  hasTrackedBeginCheckoutRef.current = true;

  pushDataLayerEvent({
    event: 'begin_checkout',
    ecommerce: {
      currency: 'GBP',
      value: checkoutValue,
      items: checkoutItems,
    },
  });

  sessionStorage.setItem(
    'printtrio_pending_purchase',
    JSON.stringify({
      payment_method: paymentMethod,
      value: checkoutValue,
      tax: Number(parseFloat(VAT || 0).toFixed(2)),
      shipping: Number(parseFloat(delivery || 0).toFixed(2)),
      coupon_code: (couponCode || '').trim(),
      items: checkoutItems,
      timestamp: Date.now(),
    })
  );
}, [products, total, VAT, delivery, couponCode, paymentMethod, isLoading]);



  // Function to handle deletion of a product from the cart
  const handleDelete = async () => {
    if (!productToDelete) return;  // Exit if no product is selected
  
    try {
      const response = await fetch(`https://backend.printtrio.co.uk/api/cart/delete-item/${productToDelete}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
  
      const data = await response.json();
  
      if (response.ok && data.success) {

        setDeleteMessage('Product has been successfully deleted.');
        // Filter out the deleted product
        setProducts(products.filter((product) => product.id !== productToDelete));
        await fetchCartData();
        console.log('Product deleted successfully:', data.message);
      } else {
        setDeleteMessage('Failed to delete the product.');
       
      }
    } catch (error) {
      setDeleteMessage('An error occurred while deleting the product.');
    }
  
    // Close the modal after deletion
    setShowDeleteConfirm(false);
    setTimeout(() => setDeleteMessage(''), 5000);
  };
  

  const handleDuplicate = async () => {
    if (!productToDuplicate) return;  // Exit if no product is selected
    
    try {
      const response = await fetch(`https://backend.printtrio.co.uk/api/cart/duplicate-item/${productToDuplicate}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
    
      const data = await response.json();
    
      if (response.ok && data.success) {
        console.log('Product duplicated successfully:', data.message);
        setDuplicateMessage('Product has been successfully duplicated.');
        
        // Fetch the updated cart data after successful duplication
        await fetchCartData();  // This will refresh the products list
        
      } else {
        setDuplicateMessage('Failed to duplicate the product.');
      }
    } catch (error) {
      setDuplicateMessage('An error occurred while duplicating the product.'); 
    }
  
    // Close the modal after duplication
    setShowDuplicateConfirm(false);

    setTimeout(() => setDuplicateMessage(''), 5000);
  };
  
  const handleSaveForLater = async () => {
    if (!productToSaveForLater) return;
    
    try {
      const response = await fetch('https://backend.printtrio.co.uk/api/cart/add-to-saved-item-from-cart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ item_id: productToSaveForLater }),
      });
  
      const data = await response.json();
  
      if (response.ok && data.success) {
        console.log('Item saved for later successfully:', data.message);
        setSaveForLaterMessage('Product has been successfully saved for later.');
        await fetchCartData(); // Optionally refresh the cart
      } else {
        setSaveForLaterMessage('Failed to save the product for later.');
      }
    } catch (error) {
      setSaveForLaterMessage('An error occurred while saving the product.');
    }
  
    setShowSaveForLaterConfirm(false); // Close the modal after saving
  };

  useEffect(() => {
    if (saveForLaterMessage) {
      const timer = setTimeout(() => setSaveForLaterMessage(''), 5000); // Clear message after 5 seconds
      return () => clearTimeout(timer); // Cleanup timer
    }
  }, [saveForLaterMessage]);
  
  
  const uploadFiles = async (productId, file1, file2) => {
    const formData = new FormData();
    formData.append('file1', file1 || '');  // Add file1 to form data
    formData.append('file2', file2 || '');  // Add file2 to form data
  
    try {
      const response = await fetch(`https://backend.printtrio.co.uk/api/cart/upload-file/${productId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,  // Add token in headers
        },
        body: formData,
      });
  
      const data = await response.json();
  
      if (response.ok && data.success) {
        // Optionally re-fetch the cart to reflect uploaded images
        await fetchCartData();
      } else {
        console.error('File upload failed:', data.errors || 'Unknown error');
      }
    } catch (error) {
      console.error('Error during file upload:', error);
    }
  };
  
  
  
  
  const handleFileChange = (e) => {
    const selectedFiles = e.target.files; // Get selected files
    setFilesToUpload({
      file1: selectedFiles[0] || null, // First selected file (if exists)
      file2: selectedFiles[1] || null, // Second selected file (if exists)
    });
  };
  
  
  
  const handleUploadFiles = async () => {
    // Check if the user selected at least one file
    if (!filesToUpload.file1 && !filesToUpload.file2) {
      setUploadErrorMessage('Please select at least one file to upload.');
      return;
    }
  
    // Proceed with the upload if files are selected
    if (selectedProduct) {
      setIsUploading(true); 
      await uploadFiles(selectedProduct, filesToUpload.file1, filesToUpload.file2);
  
      // Fetch the updated cart data after a successful upload
      await fetchCartData();  // Refresh the cart to reflect the uploaded files
  
      // Reset file inputs and modal state
      setShowUploadModal(false);  // Close the modal after upload
      setFilesToUpload({ file1: null, file2: null });  // Reset file inputs
      setUploadErrorMessage('');  // Clear any previous error
      setIsUploading(false);
    }
  };
  
  
  
  
  
  
    // Function to toggle dropdown for a specific product
    const toggleDropdown = (productId) => {
      setOpenDropdown(openDropdown === productId ? null : productId);
    };

  const getDisplayedFieldsCount = (product) => {
    let count = 0;
  
    if (product.quantity) count++;
    if (product.lamination) count++;
    if (product.size) count++;
    if (product.paper_type) count++;
    if (product.paper_weight) count++;
    if (product.printed_side) count++;
    if (product.orientation) count++;
    if (product.foiling_color) count++;
    if (product.deliver_date) count++;
    if (product.corner_cut_style) count++;
    if (typeof product.white_color !== 'undefined') count++;
    if (typeof product.spot_UV !== 'undefined') count++;
  
    return count;
  };


  const handleEditClick = (id, productId, product) => {
    // Navigate to Product page and pass along the full product data
    navigate('/product', {
      state: {
        id,
        productId,
        product
      }
    });
  };
  
 
  const handleCheckout = async () => {
    const inactive = products.find(p => p.product && p.product.is_active !== 1);
    if (inactive) {
      const name = inactive.product.name || 'This item';
      setUnavailableMessage(
        `"${name}" is currently unavailable. Please save it from your cart to proceed.`
      );
      return;
    }
    if (!selectedDeliveryAddress || !selectedBillingAddress) {
      setCheckoutError('Please select both delivery and billing addresses.');
      return;
    }
  
    try {
      setIsProcessingCheckout(true);
      setCheckoutError('');

      const formData = new FormData();
      formData.append('payment_method', paymentMethod);
      formData.append('delivery_address', selectedDeliveryAddress.id);
      formData.append('billing_address', selectedBillingAddress.id);
   const code = (couponCode || '').trim();

if (code) {
  formData.append('coupon_code', code);   
  formData.append('coupon_id', '');      
} else {
  formData.append('coupon_code', '');     
  formData.append('coupon_id', '');      
}
      formData.append('sub_total', subtotal);
      formData.append('VAT', VAT);
      formData.append('delivery', delivery);
      formData.append('total_order', total);
  // let entries = '';
  //   for (const [key, value] of formData.entries()) {
  //     entries += `${key}: ${value}\n`;
  //   }
  //   alert(`Checkout Data:\n${entries}`);
      const response = await fetch('https://backend.printtrio.co.uk/api/order/create-order', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`, // Include Authorization header
        },
        body: formData,
      });
  
      const data = await response.json();
      if (response.ok) {
        sessionStorage.setItem(
          'printtrio_pending_purchase',
          JSON.stringify({
            payment_method: paymentMethod,
            value: Number(parseFloat(total || 0).toFixed(2)),
            tax: Number(parseFloat(VAT || 0).toFixed(2)),
            shipping: Number(parseFloat(delivery || 0).toFixed(2)),
            coupon_code: (couponCode || '').trim(),
            items: products.map(buildCartTrackingItem),
            timestamp: Date.now(),
          })
        );

        if (paymentMethod === 'stripe' && data.url) {
          // Redirect to Stripe URL
          window.location.href = data.url;
        } else if (paymentMethod === 'paypal' && data.approval_url) {
          // Redirect to PayPal approval URL
          window.location.href = data.approval_url;
        } else {
          console.error('API Response Error:', data);
          setCheckoutError(data.message || 'Failed to initiate checkout. Please try again.');
        }
      } else {
        console.error('API Response Error:', data);
        setCheckoutError(data.message || 'Failed to initiate checkout. Please try again.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setCheckoutError('An error occurred during checkout. Please try again.');
    } finally {
      setIsProcessingCheckout(false);
    }
  };
  
  
  
  
  
  // useEffect(() => {
  //   const fetchPaymentStatus = async () => {
  //     const queryParams = new URLSearchParams(window.location.search);
  //     const sessionId = queryParams.get('session_id');
  
  //     if (sessionId) {
  //       try {
  //         const response = await fetch(`https://backend.printtrio.co.uk/api/stripe/success${window.location.search}`, {
  //           method: 'GET',
  //           headers: {
  //             'Content-Type': 'application/json',
  //             Authorization: `Bearer ${token}`,
  //           },
  //         });
  
  //         const paymentData = await response.json();
  
  //         // Check the payment status from the response
  //         if (paymentData?.status === 'success') {
  //           // Payment was successful, redirect to homepage
  //           navigate('/');
  //         } else {
  //           // Payment was not successful, redirect back to cart
  //           navigate('/Cart');
  //         }
  
  //       } catch (error) {
  //         console.error('Error checking payment status:', error);
  //         // On error, redirect to cart
  //         navigate('/Cart');
  //       }
  //     }
  //   };
  
  //   fetchPaymentStatus();
  // }, [navigate, token]);
  
  
  
  
  
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponMessage('Please enter a coupon code.');
      return;
    }
  
    try {
      const response = await fetch('https://backend.printtrio.co.uk/api/cart/user-cart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ coupon_code: couponCode }), // Pass the coupon code
      });
  
      const data = await response.json();
  
      if (response.ok && data.success) {
        // Update cart details with the response data
        if (data?.data?.cartItems) {
        setProducts(data.data.cartItems);
      }
        setSubtotal(data.data.sub_total);
        setDelivery(data.data.delivery);
        setVAT(data.data.VAT);
        setTotal(data.data.total_order);
        setCouponMessage('Coupon applied successfully!'); // Success message
        
      } else {
        setCouponMessage(data.errors || data.message || 'Failed to apply coupon.');
      }
    } catch (error) {
      console.error('Error applying coupon:', error);
      setCouponMessage(error.message);
    }
  };
  
// Clear coupon message after 5 seconds
useEffect(() => {
  if (couponMessage) {
    const timer = setTimeout(() => {
      setCouponMessage(''); // Clear the message
    }, 5000); // 5 seconds timeout

    return () => clearTimeout(timer); // Cleanup on component unmount or message change
  }
}, [couponMessage]);
  

  return (
    <>
      <Navbar />
      {isLoading ? (
      <div className="flex justify-center items-center h-screen">
        {/* simple Tailwind spinner */}
        <svg
          className="animate-spin h-12 w-12 text-pink-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          />
        </svg>
      </div>
    ) : cartIsEmpty ? (
  // Display empty cart design
  <div className="flex flex-col items-center justify-center p-8 mt-10 mb-10 bg-[#F4F4F4] min-h-[500px] max-w-screen-2xl mx-auto px-4 xl:px-20 relative">
    <p className="text-gray-500 text-lg mb-4">
    Discover the Finest in Print Quality. Bring Your Ideas to Life with Exceptional Craftsmanship.
    </p>
    <button 
    className="px-6 py-3 border-2 border-pink-500 rounded-full text-pink-500 font-semibold hover:bg-pink-500 hover:text-white transition-colors"
    onClick={() => navigate("/")}
    >
      Shop Now
    </button>
  </div>
) : (

      <div className="p-12  mt-10 mb-10 bg-[#F4F4F4] min-h-screen max-w-screen-2xl mx-auto px-4 xl:px-20 relative">
        <h1 className="text-xl lg:text-2xl font-bold mb-6">
          You Have {products.length} Item(s) In Your Cart
        </h1>

        {/* Error Message */}
        {error && <p className="text-red-500 mb-4">{error}</p>}

        {/* Cart Products Section */}
   <div className="space-y-4 mb-8">
  {products.map((product) => (
    <div
      key={product.id}
      className="relative bg-[#E7EEF5] border border-[#95CFF9] rounded-lg flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0 p-4"
    >
{product.product?.is_active === 0 && (
  <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center rounded-lg z-10 pointer-events-none">
    <p className="text-white text-lg font-semibold text-center px-4">
      This product is currently unavailable.
    </p>
  </div>
)}


      {/* Dropdown Menu in Top Left */}
      <div className="absolute top-4 right-4 z-20 pointer-events-auto">
        <div className="relative">
          <button
            className="text-pink-600 focus:outline-none"
            onClick={() => toggleDropdown(product.id)} // Toggle dropdown for specific product
          >
            <img src={options} style={{ width: '24px', height: '24px' }} alt="options" />
          </button>

          {/* Conditionally render dropdown content */}
          {openDropdown === product.id &&  (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
              <ul>
              {!product.is_free_sample &&  product.product?.is_active === 1 && (
                <li
                  className="p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleEditClick(product.id, product.product_id, product)}  // Add onClick to Edit
                >
                  Edit
                </li>
                 )}
                <li
                  className="p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    console.log("Opening delete confirmation for product:", product.id);
                    setProductToDelete(product.id);
                    setShowDeleteConfirm(true);
                  }}
                >
                  Delete
                </li>
                {showDeleteConfirm && (
  <div className="fixed inset-0 bg-black bg-opacity-10 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <h2 className="text-lg font-bold mb-4">Are you sure you want to delete this item?</h2>
      <div className="flex justify-end space-x-4">
        <button
          className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
          onClick={() => setShowDeleteConfirm(false)} // Cancel button
        >
          Cancel
        </button>
        <button
          className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
          onClick={handleDelete} // Confirm delete
        >
          Delete
        </button>
      </div>
    </div>
  </div>
)}



{!product.is_free_sample && product.product?.is_active === 1 && (
                <li
                  className="p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    setProductToDuplicate(product.id);  // Ensure the correct product ID is being set
                    setShowDuplicateConfirm(true);      // Show confirmation modal
                  }}
                >
                  Copy (Duplicate)
                </li>
                 )}
                {showDuplicateConfirm && (
  <div className="fixed inset-0 bg-black bg-opacity-10 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <h2 className="text-lg font-bold mb-4">Duplicate Product</h2>
      <p>Are you sure you want to duplicate this product?</p>
      <div className="flex justify-end space-x-4 mt-4">
        <button
          className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
          onClick={() => setShowDuplicateConfirm(false)} // Close modal
        >
          Cancel
        </button>
        <button
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
          onClick={handleDuplicate} // Trigger duplicate logic
        >
          Confirm
        </button>
      </div>
    </div>
  </div>
)}

{duplicateMessage && (
  <div className="fixed inset-0 bg-black bg-opacity-10 flex items-center justify-center z-50">
    <div
      className={`p-6 rounded-lg shadow-lg ${
        duplicateMessage.includes('successfully')
          ? 'bg-green-100 text-green-700'
          : 'bg-red-100 text-red-700'
      }`}
    >
      <p className="text-lg font-semibold">{duplicateMessage}</p>
    </div>
  </div>
)}

                <li
                  className="p-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    setProductToSaveForLater(product.id);  // Set the product to be saved
                    setShowSaveForLaterConfirm(true);      // Show the confirmation modal
                  }}
                >
                  Save For Later
                </li>
                {showSaveForLaterConfirm && (
  <div className="fixed inset-0 bg-black bg-opacity-10 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <h2 className="text-lg font-bold mb-4">Save Product For Later</h2>
      <p>Are you sure you want to save this product for later?</p>
      <div className="flex justify-end space-x-4 mt-4">
        <button
          className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
          onClick={() => setShowSaveForLaterConfirm(false)} // Close modal
        >
          Cancel
        </button>
        <button
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
          onClick={() => {
            handleSaveForLater();
            setShowSaveForLaterConfirm(false);
          }}
        >
          Confirm
        </button>
      </div>
    </div>
  </div>
)}
{saveForLaterMessage && (
  <div className="fixed inset-0 bg-black bg-opacity-10 flex items-center justify-center z-50">
    <div
      className={`p-6 rounded-lg shadow-lg ${
        saveForLaterMessage.includes('successfully')
          ? 'bg-green-100 text-green-700'
          : 'bg-red-100 text-red-700'
      }`}
    >
      <p className="text-lg font-semibold">{saveForLaterMessage}</p>
    </div>
  </div>
)}

                
              </ul>
              
            </div>
            
          )}
          
        </div>
      </div>
      
      {deleteMessage && (
  <div className="fixed inset-0 bg-black bg-opacity-10 flex items-center justify-center z-50">
    <div
      className={`relative p-6 rounded-lg shadow-lg ${
        deleteMessage.includes('successfully')
          ? 'bg-green-100 text-green-700'
          : 'bg-red-100 text-red-700'
      }`}
    >
      <button
        onClick={() => setDeleteMessage('')}
        className="absolute top-2 right-2 text-xl leading-none"
      >
        ×
      </button>

      <p className="text-lg font-semibold">{deleteMessage}</p>
    </div>
  </div>
)}

      {/* Left Section: Product Image */}
      <div className="relative bg-[#EFEFEF] p-4 lg:p-6 flex-col w-full lg:w-1/5 flex justify-center items-center mx-auto text-center">
  {product.is_free_sample === 1 ? (
    // 1️⃣ Free sample always shows its sample image
    <img
      src={product.free_sample.image}
      alt={product.free_sample.name}
      className="object-cover rounded-lg w-full h-full"
      style={{ cursor: "default" }}
    />
  ) : (() => {
    // helper to detect image file extensions
    const isImageUrl = (url) =>
      /\.(jpe?g|png|gif|bmp|svg|webp|tiff?)$/i.test(url);

    const { file1, file2, product: p } = product;
    const defaultImg = p.images[0]?.image;

    // 2️⃣ Neither file uploaded → placeholder upload button
    if (!file1 && !file2) {
      return (
        <img
          src={UploadArtwork}
          alt="Upload artwork"
          className="object-cover rounded-lg mx-auto cursor-pointer"
          onClick={() => {
            setSelectedProduct(product.id);
            setShowUploadModal(true);
          }}
        />
      );
    }

    // 3️⃣ Both files exist and are images → show Swiper of those URLs
    if (file1 && file2 && isImageUrl(file1) && isImageUrl(file2)) {
      return (
        <Swiper
          modules={[Pagination, Autoplay]}
          pagination={{ clickable: true }}
          spaceBetween={30}
          slidesPerView={1}
          autoplay={false}
          speed={2000}
          loop={false}
          className="w-full"
        >
          {[file1, file2].map((src, idx) => (
            <SwiperSlide key={idx}>
              <div className="relative w-full h-[240px]">
                <img
                  src={src}
                  alt={p.name}
                  className="object-cover rounded-lg w-full h-full"
                />
                <div className="absolute top-2 right-2">
                  <button
                    onClick={() => {
                      setSelectedProduct(product.id);
                      setShowUploadModal(true);
                    }}
                    className="flex items-center justify-center w-5 h-5"
                    title="Upload Files"
                  >
                    <img
                      src={edit}
                      alt="Upload artwork"
                      className="object-cover rounded-lg cursor-pointer"
                    />
                  </button>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      );
    }

    // 4️⃣ Only one image-file uploaded → show that one, with upload button
    if ((file1 && isImageUrl(file1)) || (file2 && isImageUrl(file2))) {
      const src = isImageUrl(file1) ? file1 : file2;
      return (
        <div className="relative w-full lg:w-[240px] h-[240px]">
          <img
            src={src}
            alt={p.name}
            className="object-cover rounded-lg w-full h-full"
          />
          <div className="absolute top-3 right-3">
            <button
              onClick={() => {
                setSelectedProduct(product.id);
                setShowUploadModal(true);
              }}
              className="flex items-center justify-center w-5 h-5"
              title="Upload Files"
            >
              <img
                src={edit}
                alt="Upload artwork"
                className="object-cover rounded-lg cursor-pointer"
              />
            </button>
          </div>
        </div>
      );
    }

    // 5️⃣ Fallback: non-image files or mixed → product’s default image (no upload)
    return (
      <div className="relative w-full lg:w-[240px] h-[240px]">
        <img
          src={defaultImg}
          alt={p.name}
          className="object-cover rounded-lg w-full h-full"
        />
      </div>
    );
  })()}

  {/* upload-error link stays visible if there was an error */}
  {uploadError[product.id] && (
    <p
      className="text-red-500 text-sm mt-2 cursor-pointer"
      onClick={() => {
        setSelectedProduct(product.id);
        setShowUploadModal(true);
      }}
    >
      {uploadError[product.id]}
    </p>
  )}
</div>




      {showUploadModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white w-full max-w-md p-6 rounded-lg shadow-lg">
      <h2 className="text-lg font-bold mb-4 text-center">Upload Files</h2>
      <p className="text-gray-600 mb-4 text-center">Upload the required files for this product.</p>

      <input
        type="file"
        multiple
        onChange={handleFileChange} // Handle file selection
        className="block w-full mb-4"
      />

      {uploadErrorMessage && (
        <p className="text-red-500 text-sm mb-4">{uploadErrorMessage}</p>
      )}

      <div className="flex justify-end space-x-4">
      {isUploading ? (
    // Show spinner during upload
    <div className="flex justify-center w-full">
      <svg
        className="animate-spin h-12 w-12 text-pink-500"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v8H4z"
        />
      </svg>
    </div>
  ) : (
    <>
        <button
          className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
          onClick={() => setShowUploadModal(false)} // Close the modal
        >
          Cancel
        </button>
        <button
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
          onClick={handleUploadFiles} // Trigger file upload
        >
          Upload
        </button>
        </>
  )}
      </div>
    </div>
  </div>
)}


      {/* Right Section: Product Details */}
      <div className="flex-grow p-4 lg:p-6 w-full lg:w-4/5 flex flex-col lg:flex-row justify-between items-start lg:items-center">
        <div className={`text-gray-700 space-y-2 ${getDisplayedFieldsCount(product) > 6 ? 'grid grid-cols-2 gap-4' : ''}`}>
          <h2 className="text-xl font-bold text-gray-900 col-span-2">{product.product?.name || product.free_sample?.name}</h2>

          {/* Render other fields responsively */}
          {product.quantity && <p className="text-sm">Quantity: {product.quantity}</p>}
          {product.lamination && <p className="text-sm">Lamination: {product.lamination || 'None'}</p>}
          {product.size && <p className="text-sm">Size: {product.size}</p>}
          {product.paper_type && <p className="text-sm">Paper Type: {product.paper_type}</p>}
          {product.paper_weight && <p className="text-sm">Paper Weight: {product.paper_weight}</p>}
          {product.printed_side && product.free_sample_id !== 1 && (
  <p className="text-sm">Printed Sides: {product.printed_side}</p>
)}
          {product.orientation && <p className="text-sm">Orientation: {product.orientation}</p>}
          {product.foiling_color && <p className="text-sm">Foiling Color: {product.foiling_color}</p>}
          {product.deliver_date && <p className="text-sm">Delivery Date: {new Date(product.deliver_date).toLocaleDateString()}</p>}
          {product.corner_cut_style && <p className="text-sm">Corners: {product.corner_cut_style || 'N/A'}</p>}
          {product.white_color === 1 && <p className="text-sm">White Color: Yes</p>}

{product.spot_UV === 1 && <p className="text-sm">Spot UV: Yes</p>}
        </div>

        {/* Price Section */}
        <div className="mt-4 lg:mt-0 lg:ml-10 self-end text-left lg:text-right">
          <p className="text-sm font-normal text-gray-700 mb-1">Price:</p>
          <p className="text-lg font-bold text-gray-900">
          £{product.free_sample_id === 1
      ? parseFloat(product.free_sample.price).toFixed(2)
      : parseFloat(product.total_price_with_vat).toFixed(2)}{' '}
            <span className="text-sm font-normal text-gray-500">(Inc VAT)</span>
          </p>
        </div>
      </div>
    </div>
  ))}
</div>


        {/* Apply Voucher Section */}
        <div className="bg-pink-500 p-4 rounded-lg text-white mb-8">
  <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0 lg:ml-20 lg:mr-20">
    <p className="text-lg font-semibold lg:mr-4 lg:mb-0 mb-2">Apply Voucher</p>
    
    <div className="relative flex-1 w-full lg:w-auto mx-auto lg:mx-0">
  <input
    type="text"
    value={couponCode}
    onChange={(e) => {
      setCouponCode(e.target.value);
      console.log("code:", e.target.value);
    }}
    placeholder="Enter Voucher Code"
    className="bg-white rounded-lg p-3 pr-10 placeholder-gray-400 focus:outline-none text-black"
  />
  {couponCode && (
    <button
      type="button"
      onClick={() => {
        setCouponCode("");
        setCouponMessage("");
        fetchCartData();
      }}
      className="absolute inset-y-0 left-52 flex items-center text-black hover:text-gray-600"
    >
      X
    </button>
  )}
</div>


    
    <button 
    type="button"
    onClick={handleApplyCoupon}
    className="w-full lg:w-auto px-6 py-3 border-2 border-white rounded-full text-white font-semibold hover:bg-white hover:text-pink-500 transition-colors"
    >
      Apply
    </button>
  </div>

  {couponMessage && (
    <p
      className={`mt-4 text-sm font-semibold ${
        couponMessage.includes('success') ? 'text-green-200' : 'text-red-200'
      }`}
    >
      {couponMessage}
    </p>
  )}
</div>


        {/* Order Summary Section */}
        <div className="bg-[#E7EEF5] border border-[#95CFF9] p-6 rounded-lg shadow-md text-gray-700 flex flex-col lg:flex-row justify-between items-start space-y-6 lg:space-y-0">
  {/* Left Section: Order Summary */}
  <div className="w-full lg:w-1/2 pr-4 flex flex-col justify-between">
    <h3 className="text-lg font-bold mb-4">Order Summary</h3>

    <div className="mt-6 lg:mt-20">
    <div>
  {checkoutError && <p className="text-red-500 mb-4">{checkoutError}</p>}

  <button
    className={`w-full lg:w-40 flex items-center justify-center ${
      isProcessingCheckout ? 'bg-gray-200 text-gray-400' : 'bg-white text-black'
    } border border-black py-3 rounded-lg text-lg hover:bg-gray-100`}
    onClick={handleCheckout}
    disabled={isProcessingCheckout} // Disable button while processing
  >
    {isProcessingCheckout ? (
      <>
        Processing...
        <svg
          className="animate-spin h-5 w-5 ml-2"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8h8a8 8 0 01-16 0z"
          ></path>
        </svg>
      </>
    ) : (
      <>
        Checkout
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 ml-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M5 12h14M12 5l7 7-7 7"
          />
        </svg>
      </>
    )}
  </button>
</div>

{popupMessage && (
       <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50 ">
       <div className="bg-white p-6 rounded-lg shadow-lg text-center w-80 h-40">
            <p className="text-lg font-bold mb-4">{popupMessage}</p>
            <button
              onClick={() => setPopupMessage('')}
              className="mt-4 px-6 py-2 bg-red-500 text-white rounded-lg"
            >
              Close
            </button>
          </div>
        </div>
      )}
{unavailableMessage && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm mx-4 text-center">
      <p className="mb-4">{unavailableMessage}</p>
      <button
        onClick={() => setUnavailableMessage('')}
        className="px-4 py-2 bg-pink-500 text-white rounded hover:bg-pink-600"
      >
        OK
      </button>
    </div>
  </div>
)}



      <div className="mt-4">
        <a href="/" className="text-gray-500 underline">
          Continue Shopping
        </a>
      </div>
    </div>
  </div>

  {/* Right Section: Pricing and Address Details */}
  <div className="w-full lg:w-1/2 pl-4">
  <div className="mb-4">
  <h3 className="text-sm text-gray-600 mb-2">Select Payment Method</h3>
  <div className="flex items-center space-x-4">
    <button
      className={`px-6 py-2 border rounded-lg ${
        paymentMethod === 'stripe' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'
      }`}
      onClick={() => setPaymentMethod('stripe')}
    >
      Stripe
    </button>
    <button
      className={`px-6 py-2 border rounded-lg ${
        paymentMethod === 'paypal' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700'
      }`}
      onClick={() => setPaymentMethod('paypal')}
    >
      PayPal
    </button>
  </div>
</div>


    {/* Address Section */}
    <div className="mb-4">
      <p className="text-sm text-gray-600">Delivery To</p>
      <p className="font-semibold mb-2">Address Here</p>
      <div className="flex flex-col">
  <a
    href="#"
    className="text-gray-500 underline text-sm"
    onClick={() => {
      setAddressType('delivery');
      setShowAddressModal(true);
    }}
  >
    Select delivery address
  </a>
  {selectedDeliveryAddress && (
    <p className="mt-2 mb-2">
      <span className="text-md font-semibold">Delivery Address:</span>{' '}
      {selectedDeliveryAddress.street}, {selectedDeliveryAddress.city}, {selectedDeliveryAddress.country}
    </p>
  )}

  <a
    href="#"
    className="text-gray-500 underline text-sm"
    onClick={() => {
      setAddressType('billing');
      setShowAddressModal(true);
    }}
  >
    Select billing address
  </a>
  {selectedBillingAddress && (
    <p className="mt-2">
      <span className="text-md font-semibold">Billing Address:</span>{' '}
      {selectedBillingAddress.street}, {selectedBillingAddress.city}, {selectedBillingAddress.country}
    </p>
  )}
</div>

    </div>

    {showAddressModal && (
  <div className="fixed inset-0 bg-black bg-opacity-10 flex items-center justify-center z-50">
    <div className="bg-white w-full max-w-md p-6 rounded-lg shadow-lg m-5 md:m-0">
      <h2 className="text-xl font-bold mb-6 text-center">
        Select {addressType === 'delivery' ? 'Delivery' : 'Billing'} Address
      </h2>

      <div className="space-y-4 max-h-72 overflow-y-auto">
        {allAddresses.length > 0 ? (
          allAddresses.map((address) => (
            <div
              key={address.id}
              className="p-4 border border-gray-200 rounded-lg shadow-sm flex justify-between items-center"
            >
              <div className="flex flex-col">
                <p className="font-semibold">{address.name}</p>
                <p className="text-gray-500 text-sm">
                  {address.street}, {address.city}, {address.country}
                </p>
                <a
                  href={`tel:${String(address.phone_number || '').replace(/[^\d+]/g, '')}`}
                  className="text-sm text-blue-600 hover:underline"
                >
                  {address.phone_number}
                </a>
              </div>
              <button
                className="text-white bg-[#EC008C] px-4 py-2 rounded-lg transition-all"
                onClick={() => {
                  if (addressType === "delivery") {
                    setSelectedDeliveryAddress(address);
                  } else {
                    setSelectedBillingAddress(address);
                  }
                  setShowAddressModal(false); // Close modal
                }}
              >
                Select
              </button>
            </div>
          ))
        ) : (
          // Inline AddAddress component when no addresses exist
          <div className="text-center text-gray-600 py-6">
            <p>No saved addresses found.</p>
            <p className="mt-2">Please add a new address below.</p>
            <div className="mt-4 p-4 bg-gray-100 rounded-lg shadow-inner">
              <h3 className="text-lg font-semibold text-[#EC008C] text-center">Add New Address</h3>
              <p className="text-sm text-gray-500 text-center mb-4">Fill in the details to save a new address.</p>

              {/* Inline AddAddress Form */}
              <form onSubmit={handleSubmit} autoComplete="off" className="p-4 bg-white rounded-lg shadow-md border border-gray-200">
                <div className="grid grid-cols-1 gap-4">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Full Name *"
                    className="border rounded-lg p-2 w-full"
                    required
                  />
                                    <input
                    type="text"
                    name="building"
                    value={formData.building}
                    onChange={handleChange}
                    placeholder="Building/House number and street name*"
                    className="border rounded-lg p-2 w-full"
                    required
                  />
                                    <input
                    type="text"
                    name="apartment"
                    value={formData.apartment}
                    onChange={handleChange}
                    placeholder="Apartment, suite, unit, building, floor (optional)"
                    className="border rounded-lg p-2 w-full"
                  />
                   <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City *"
                    className="border rounded-lg p-2 w-full"
                    required
                  />

                                    <input
                    type="text"
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleChange}
                    onBlur={() => {
                      if (formData.county === "United Kingdom") {
                        handlePostcodeLookup();
                      }
                    }}
                    
                    placeholder="Postcode *"
                    className="border rounded-lg p-2 w-full"
                    required
                  />
                  {postcodeError && <p className="text-red-500 text-sm">{postcodeError}</p>}
                <div className="border rounded-lg p-1 w-full">
  <Select
    options={countryOptions}
    value={countryOptions.find(opt => opt.value === formData.county)}
      defaultValue={countryOptions.find(opt => opt.value === "United Kingdom")}
    onChange={(selected) =>
      setFormData((prev) => ({ ...prev, county: selected.value }))
    }
    placeholder="Select Country"
    classNamePrefix="select"
    styles={{
      control: (base) => ({
        ...base,
        border: 'none',
        boxShadow: 'none',
      }),
      placeholder: (base) => ({
        ...base,
        textAlign: 'left',
        color: '#9CA3AF', // Tailwind gray-400
      }),
      singleValue: (base) => ({
        ...base,
        textAlign: 'left',
      }),
      input: (base) => ({
        ...base,
        textAlign: 'left',
      }),
    }}
  />
</div>




                 
                  {/* <input
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleChange}
                    placeholder="Street *"
                    className="border rounded-lg p-2 w-full"
                    required
                  /> */}

                  {/* <input
                    type="text"
                    name="floor"
                    value={formData.floor}
                    onChange={handleChange}
                    placeholder="Floor *"
                    className="border rounded-lg p-2 w-full"
                    required
                  /> */}

                  <input
                    type="text"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                    placeholder="Phone Number *"
                    className="border rounded-lg p-2 w-full"
                    required
                  />
                  <textarea
                    name="note"
                    value={formData.note}
                    onChange={handleChange}
                    placeholder="Additional Notes (optional)"
                    className="border rounded-lg p-2 w-full h-20"
                  ></textarea>
                </div>

                <div className="flex items-center justify-between mt-4">
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      name="isDefault"
                      checked={formData.isDefault}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    Set as Default Address
                  </label>
                  <label className="flex items-center text-sm">
                    <input
                      type="checkbox"
                      name="with_logo"
                      checked={formData.with_logo}
                      onChange={handleChange}
                      className="mr-2"
                    />
                    With Logo
                  </label>
                </div>

                <div className="flex justify-between mt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`bg-[#EC008C] text-white px-6 py-2 rounded-lg hover:bg-pink-600 ${
                      isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? "Saving..." : "Save Address"}
                  </button>
                  <button
                    type="button"
                    className="bg-gray-300 px-6 py-2 rounded-lg hover:bg-gray-400"
                    onClick={() => setShowAddressModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      <div className="flex justify-center mt-6">
        <button
          className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-all"
          onClick={() => setShowAddressModal(false)}
        >
          Close
        </button>
      </div>
    </div>
  </div>
)}


    {/* Pricing Section */}
    <div className="space-y-2">
      <div className="flex justify-between">
        <span>Subtotal</span>
        <span>£{subtotal.toFixed(2)}</span>
      </div>
      <div className="flex justify-between">
        <span>Delivery</span>
        <span>£{delivery.toFixed(2)}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-sm">VAT</span>
        <span>£{VAT.toFixed(2)}</span>
      </div>
    </div>

    <div className="flex justify-between font-bold text-lg mt-4">
      <span>Total Order</span>
      <span>£{total.toFixed(2)}</span>
    </div>
  </div>
</div>

      </div>
)}

{/* {popupVisible && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg shadow-lg text-center max-w-md">
      <p className="text-lg font-bold mb-4">{popupMessage}</p>
    </div>
  </div>
)} */}

      <Footer />
    </>
  );
};

export default CartPage;
